import React, { useState, useEffect } from 'react';
import { ConsentNotice } from '@/components/ConsentNotice';
import { CountryGateNotice } from '@/components/CountryGateNotice';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Mail } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SignupPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [ageAttested, setAgeAttested] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [healthConsent, setHealthConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [signupConfig, setSignupConfig] = useState({ consent_required: true, privacy_policy_url: '/privacy-policy', legal_docs_are_drafts: true });
  const [gate, setGate] = useState({ allowed: true, invite_only: false, checked: false });
  const [inviteCode, setInviteCode] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/signup-config`)
      .then((res) => setSignupConfig(res.data))
      .catch(() => {});
    // Country gate: account creation only (this screen + the sign-up API). Fail-open if the check cannot be made.
    axios.get(`${API_URL}/api/geo/signup-eligibility`)
      .then((res) => setGate({ ...res.data, checked: true }))
      .catch(() => setGate({ allowed: true, invite_only: false, checked: true }));
  }, []);

  const consentRequired = signupConfig.consent_required;
  const isDraftLegal = signupConfig.legal_docs_are_drafts !== false;
  const policyUrl = signupConfig.privacy_policy_url || '/privacy-policy';
  const isExternalPolicy = policyUrl.startsWith('http');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isDraftLegal) {
      if (!consentChecked) {
        toast.error('You must acknowledge the pre-release notice to create an account.');
        return;
      }
    } else {
      if (consentRequired && !consentChecked) {
        toast.error('You must accept the Privacy Policy to create an account.');
        return;
      }
      if (!termsAccepted) {
        toast.error('You must accept the Terms of Use to create an account.');
        return;
      }
    }
    if (!ageAttested) {
      toast.error('You must confirm you are 18 or older to create an account.');
      return;
    }
    if (!healthConsent) {
      toast.error('You must consent to the processing of your dietary/health data to use this service.');
      return;
    }
    if (gate.invite_only && !inviteCode.trim()) {
      toast.error('An invite code is required to create an account at the moment.');
      return;
    }
    setLoading(true);
    try {
      const result = await signup(email, password, false, consentChecked, gate.invite_only ? inviteCode.trim() : null);
      try {
        const tok = sessionStorage.getItem('auth_token') || localStorage.getItem('token');
        await axios.post(`${API_URL}/api/auth/me/consents`, {
          age_attested_18_plus: true,
          terms_accepted: true,
          health_data_consent: true,
          marketing_consent: marketingConsent,
          screen: 'signup',
          notice_key: 'signup'
        }, { headers: { Authorization: `Bearer ${tok}` } });
      } catch (e) { /* consent record best-effort at signup; re-prompted if missing */ }
      toast.success('Account created! Please check your email to verify your account.');
      // Redirect to email verification page
      navigate('/verify-email');
    } catch (error) {
      const d = error.response?.data?.detail;
      if (error.response?.status === 451 && d && typeof d === 'object') {
        setGate((g) => ({ ...g, allowed: false, country: d.country, message: d.message, waitlist_notice: d.waitlist_notice }));
        return;
      }
      toast.error(typeof d === 'object' && d?.message ? d.message : (d || 'Signup failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-b from-primary/5 to-transparent">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
          <div className="flex justify-center items-center gap-2 mb-4">
            <span className="text-3xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>INSUL NUTRIENTS</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Create Account</h1>
          <p className="text-base text-muted-foreground">Start your visual food diary today</p>
        </div>

        {!gate.allowed ? (
          <CountryGateNotice gate={gate} />
        ) : (
        <div className="bg-card text-card-foreground rounded-2xl border border-border/50 shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {gate.invite_only && (
              <div className="space-y-2" data-testid="invite-code-section">
                <Label htmlFor="invite-code" className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Invite code</Label>
                <Input
                  id="invite-code"
                  data-testid="signup-invite-code-input"
                  placeholder="XXXX-XXXX-XXXX"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  required
                  className="rounded-xl border-border bg-background/50 px-4 py-3 shadow-sm uppercase tracking-widest"
                />
                <p className="text-xs text-muted-foreground">Insul Nutrients is invite-only at the moment. Each code can be used once.</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Email</Label>
              <Input
                id="email"
                data-testid="signup-email-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-xl border-border bg-background/50 focus:ring-2 focus:ring-ring focus:border-transparent px-4 py-3 shadow-sm transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Password</Label>
              <Input
                id="password"
                data-testid="signup-password-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-xl border-border bg-background/50 focus:ring-2 focus:ring-ring focus:border-transparent px-4 py-3 shadow-sm transition-all"
              />
            </div>
            <div className="space-y-3" data-testid="signup-attestations-section">
              <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-background/50 p-4">
                <Checkbox id="age-attest" data-testid="age-attestation-checkbox" checked={ageAttested}
                  onCheckedChange={(c) => setAgeAttested(c === true)} className="mt-0.5" />
                <Label htmlFor="age-attest" className="text-sm font-normal leading-snug cursor-pointer">
                  I confirm that I am 18 years of age or older
                </Label>
              </div>
              {!isDraftLegal && (
              <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-background/50 p-4">
                <Checkbox id="terms-accept" data-testid="terms-accept-checkbox" checked={termsAccepted}
                  onCheckedChange={(c) => setTermsAccepted(c === true)} className="mt-0.5" />
                <Label htmlFor="terms-accept" className="text-sm font-normal leading-snug cursor-pointer">
                  <ConsentNotice slot="signup" sub="agreement_required">I accept the <Link to="/terms" target="_blank" className="text-primary font-semibold hover:underline">Terms of Use</Link></ConsentNotice>
                </Label>
              </div>
              )}
              <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-background/50 p-4">
                <Checkbox id="health-consent" data-testid="health-data-consent-checkbox" checked={healthConsent}
                  onCheckedChange={(c) => setHealthConsent(c === true)} className="mt-0.5" />
                <Label htmlFor="health-consent" className="text-sm font-normal leading-snug cursor-pointer">
                  I expressly consent to the collection and processing of my dietary and health information (meal photos and nutrition data) to provide this service
                </Label>
              </div>
              <div className="flex items-start gap-3 rounded-xl border border-dashed border-border/60 bg-background/30 p-4">
                <Checkbox id="marketing-consent" data-testid="marketing-consent-checkbox" checked={marketingConsent}
                  onCheckedChange={(c) => setMarketingConsent(c === true)} className="mt-0.5" />
                <Label htmlFor="marketing-consent" className="text-sm font-normal leading-snug cursor-pointer text-muted-foreground">
                  Optional: send me daily meal reminder emails. You can unsubscribe anytime — this never affects your account or access.
                </Label>
              </div>
            </div>
            {isDraftLegal && (
              <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50/60 p-4" data-testid="prerelease-ack-section">
                <Checkbox
                  id="prerelease-ack"
                  data-testid="prerelease-ack-checkbox"
                  checked={consentChecked}
                  onCheckedChange={(checked) => setConsentChecked(checked === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="prerelease-ack" className="text-sm font-normal leading-snug text-foreground/90 cursor-pointer">
                  <span className="font-semibold block mb-1">Pre-release acknowledgement</span>
                  I understand that Insul Nutrients is in active development and that its{' '}
                  <Link to="/terms" target="_blank" className="text-primary font-semibold hover:underline">Terms of Use</Link> and{' '}
                  <Link to={policyUrl} target="_blank" className="text-primary font-semibold hover:underline" data-testid="privacy-policy-link">Privacy Policy</Link>{' '}
                  are drafts that are not yet in effect. In the meantime, my data (email, profile details, meal photos
                  and nutrition logs) is collected only to provide the service, meal photos are deleted after 7 days,
                  and I can export my data or delete my account at any time. I will be asked to review and accept the
                  final Terms and Privacy Policy when they are published.
                </Label>
              </div>
            )}
            {!isDraftLegal && consentRequired && (
              <div className="flex items-start gap-3 rounded-xl border border-border/60 bg-background/50 p-4" data-testid="privacy-consent-section">
                <Checkbox
                  id="privacy-consent"
                  data-testid="privacy-consent-checkbox"
                  checked={consentChecked}
                  onCheckedChange={(checked) => setConsentChecked(checked === true)}
                  className="mt-0.5"
                />
                <Label htmlFor="privacy-consent" className="text-sm font-normal leading-snug text-foreground/90 cursor-pointer">
                  I have read and accept the{' '}
                  {isExternalPolicy ? (
                    <a href={policyUrl} target="_blank" rel="noopener noreferrer" className="text-primary font-semibold hover:underline" data-testid="privacy-policy-link">Privacy Policy</a>
                  ) : (
                    <Link to={policyUrl} target="_blank" className="text-primary font-semibold hover:underline" data-testid="privacy-policy-link">Privacy Policy</Link>
                  )}
                </Label>
              </div>
            )}
            <Button
              data-testid="signup-submit-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-full px-8 py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all active:scale-95 bg-gradient-to-r from-primary to-[#8FD14F]"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-primary font-semibold hover:underline">
              Login
            </Link>
          </p>
        </div>
        )}
      </motion.div>

      {/* Footer */}
      <footer className="absolute bottom-4 left-0 right-0 text-center">
        <Link 
          to="/contact-us" 
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1 font-medium"
          data-testid="contact-us-link"
        >
          <Mail className="w-4 h-4" />
          Contact Us
        </Link>
      </footer>
    </div>
  );
};

export default SignupPage;